import { Divider, Icon, MenuItem, Select, Tooltip } from "@material-ui/core";
import { MuiThemeProvider } from "@material-ui/core/styles";
import { withStyles } from "@material-ui/styles";
import classNames from "classnames";
import React, { Component, useEffect } from "react";
import { Subscription } from "rxjs";
import styled from "styled-components";

import { WorkspacePermissionSubjectType, WorkspaceResourceType } from "common/interfaces";
import CommentsController from "js/controllers/CommentsController";
import PresentationPermissionsController from "js/controllers/PresentationPermissionsController";
import WorkspaceController, { getUserPermissionsToResource } from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import getLogger, { LogGroup } from "js/core/logger";
import { ds } from "js/core/models/dataService";
import { Key } from "js/core/utilities/keys";
import { trackActivity } from "js/core/utilities/utilities";
import PresentationEditorController from "js/editor/PresentationEditor/PresentationEditorController";
import { app } from "js/namespaces";
import { FlexBox } from "js/react/components/LayoutGrid";
import { ShowSnackBar } from "js/react/components/Dialogs/SnackBar";
import { dialogTheme } from "js/react/materialThemeOverrides";
import { withFirebaseUser } from "js/react/views/Auth/FirebaseUserContext";

import Comment from "./Comment";
import { getCommentsPaneLists } from "./commentUtils";
import { COMMENT_NOTIFICATIONS_SECTION_ID } from "js/react/views/PresentationSettings/panes/CollaboratePane";
import { CommentFilters } from "../../../../common/constants";

const logger = getLogger(LogGroup.COMMENTS);

const styleSelect = theme => ({
    icon: {
        color: "#A9A9A9",
    },
    select: {
        paddingLeft: 0,
        color: "#666",
        display: "flex",
        alignItems: "center",
        backgroundColor: "transparent",
        "&:selected": {
            backgroundColor: "transparent",
        },
        "&:focus": {
            backgroundColor: "transparent",
        },
    },
    nativeInput: {
        width: "auto",
    },
});

const StyledMenuItem = styled(MenuItem)`
    &&& {
        color: #666;
        font-size: 12px;

        &.Mui-selected {
            color: #666;
        }        
    }
`;

const CommentFilterSelect = styled(withStyles(styleSelect)(Select))`
    &&& {
        margin-top: 8px;
    }
`;

const CommentsList = styled.div`
    overflow-y: auto;
    flex: 1;
    min-height: 0;
    margin-top: 10px;
`;

const DarkSelect = styled.span`
    font-size: 12px;
    color: #666;
`;

const NotificationSettingsButton = styled(Icon)`
    &&& {
        margin-left: auto;
        margin-top: 8px;
        cursor: pointer;
        font-size: 20px;
        color: #888;

        &:hover {
            color: #29b2e5;
        }
    }
`;

class CommentsPane extends Component {
    constructor(props) {
        super(props);

        // The pane is remounted when navigating between slides in the editor, so the host can
        // seed the filter with the last selection (and get notified when it changes) to keep it
        const { initialCommentFilter } = props;
        const isKnownFilter = Object.values(CommentFilters).includes(initialCommentFilter);

        this.state = {
            commentFilter: isKnownFilter ? initialCommentFilter : CommentFilters.CurrentSlide,
            canEditPresentation: false,
            assignedUser: null,
            isPresentationOwner: false,
        };

        this.user = app.user.getAuthUser();
        this.commentListRef = React.createRef();
        this.subs = new Subscription();
        this.handleGlobalKeyDown = null;
    }

    get teammates() {
        const { byViewer } = this.props;
        if (byViewer) {
            return [];
        }

        // Explicitly get from WorkspaceController as users prop overlap with PresentationPermissionsController
        const users = WorkspaceController.users;

        return users
            .map(user => ({
                uid: user.uid,
                displayName: user.displayName,
                email: user.email,
                photoURL: user.photoURL
            }));
    }

    get collaborators() {
        const { byViewer } = this.props;
        if (byViewer) {
            return [];
        }

        const { permissions, invites } = this.props;

        const collaborators = permissions
            .filter(p => p.subjectType === WorkspacePermissionSubjectType.USER)
            .map(p => WorkspaceController.users.some(u => u.uid === p.subjectId) ? { ...WorkspaceController.users.find(u => u.uid === p.subjectId), permission: p } : null)
            .filter(Boolean)
            .map(user => ({
                uid: user.uid,
                displayName: user.displayName,
                email: user.email,
                photoURL: user.photoURL,
                permissionType: user.permission.write.object ? "edit" : "view",
                isPending: false
            }));

        permissions.filter(p => p.subjectType === WorkspacePermissionSubjectType.USER_GROUP)
            .forEach(p => {
                const group = WorkspaceController.userGroups.find(g => g.id === p.subjectId);
                if (!group) {
                    return;
                }
                // Groups can be nested (e.g. a team folder's group contains the owners/members/librarians
                // groups rather than the users themselves), so resolve membership transitively -
                // a flat groupIds check misses everyone who has access via a folder
                const groupUsers = WorkspaceController.getUsersInGroup(group.id).filter(user => !collaborators.some(c => c.uid === user.uid));
                groupUsers.forEach(user => {
                    collaborators.push({
                        uid: user.uid,
                        displayName: user.displayName,
                        email: user.email,
                        photoURL: user.photoURL,
                        permissionType: p.write.object ? "edit" : "view",
                        isPending: false
                    });
                });
            });

        const invitedCollaborators = invites
            .map(i => ({
                uid: i.inviteeUid,
                displayName: i.inviteeEmail,
                email: i.inviteeEmail,
                photoURL: null,
                permissionType: i.permissions.write ? "edit" : "view",
                isPending: true
            }));

        return [...collaborators, ...invitedCollaborators];
    }

    async componentDidMount() {
        const {
            assignedToSlideUser$,
        } = ds.getObservables();

        this.subs.add(
            assignedToSlideUser$.subscribe(assignedUser => this.setState({ assignedUser }))
        );

        const presentation = ds.selection.presentation;
        const isPresentationOwner = presentation?.get("userId") === this.user.uid;
        let canEditPresentation = isPresentationOwner;
        if (!canEditPresentation) {
            const permissions = await getUserPermissionsToResource({
                workspaceId: AppController.workspaceId,
                resourceId: presentation.id,
                resourceType: presentation.get("isTemplate") ? WorkspaceResourceType.PRESENTATION_TEMPLATE : WorkspaceResourceType.PRESENTATION
            });
            canEditPresentation = permissions.write.object;
        }

        this.setState({
            canEditPresentation,
            isPresentationOwner
        });

        await PresentationEditorController.setHandleKeyEvents(false);

        this.handleGlobalKeyDown = evt => {
            if (evt.which === Key.ESCAPE) {
                evt.preventDefault();
                evt.stopPropagation();
                this.props.close && this.props.close();
            }

            if (evt.metaKey || evt.ctrlKey) {
                switch (evt.which) {
                    case Key.KEY_C:
                        document.execCommand("copy");
                        break;
                    case Key.KEY_X:
                        document.execCommand("cut");
                        break;
                    case Key.KEY_F:
                        if (PresentationEditorController.isInitialized) {
                            PresentationEditorController.handleShowSearchForText(true);
                        }
                        evt.stopPropagation();
                        evt.preventDefault();
                        break;
                }
            }
        };
        window.addEventListener("keydown", this.handleGlobalKeyDown);

        trackActivity("Comments", "Opened", null, null, { location: this.props.surface, byViewer: this.props.byViewer });
    }

    componentWillUnmount() {
        this.subs.unsubscribe();
        window.removeEventListener("keydown", this.handleGlobalKeyDown);

        PresentationEditorController.setHandleKeyEvents(true);

        trackActivity("Comments", "Closed", null, null, { location: this.props.surface, byViewer: this.props.byViewer });
    }

    // Common analytics properties for every comment event. User ID, plan type, user role and
    // workspace are attached globally by the analytics layer (analytics/util.track merges the
    // tracked state into every event), so here we only add the comment-specific dimensions.
    commentEventProps = ({ slideId, commentId }) => {
        const { presentation, byViewer, surface } = this.props;
        return {
            ...presentation.getAnalytics(),
            slide_id: slideId,
            comment_id: commentId,
            byViewer,
            // Internal (editor collaborators) vs Viewer (end-viewer) comment thread
            comment_type: byViewer ? "viewer" : "internal",
            // Surface where the action happened: the editor comments pane vs the player
            location: surface,
            // Comments are slide-level today; kept explicit so the dimension already exists
            // if/when element-level comments are introduced
            comment_scope: "slide",
        };
    }

    postComment = async (text, taggedUsers, pendingTaggedUsers) => {
        try {
            const { byViewer, currentSlide } = this.props;
            const slideId = currentSlide.id;

            // Creating a proper signature for the comment to be posted
            const comment = {
                text,
                taggedUsers,
                pendingTaggedUsers,
                byViewer,
            };

            const { id: commentId } = await CommentsController.createComment(slideId, comment);

            const props = this.commentEventProps({ slideId, commentId });
            trackActivity("Comment", "Created", null, null, props, { audit: true });
        } catch (err) {
            logger.error(err, "[comments] postComment() failed");
            // Rethrow so the composer keeps the draft and resets its spinner instead of clearing
            throw err;
        }
    }

    removeComment = async comment => {
        try {
            const commentId = comment.id;
            const slideId = comment.slideId;

            await CommentsController.deleteComment(commentId, slideId);

            ShowSnackBar({ message: "Comment deleted" });

            const props = this.commentEventProps({ slideId, commentId });
            trackActivity("Comment", "Deleted", null, null, props, { audit: true });
        } catch (err) {
            logger.error(err, "[comments] removeComment() failed");
        }
    }

    updateComment = async (comment, text, taggedUsers, pendingTaggedUsers) => {
        try {
            const commentId = comment.id;
            const slideId = comment.slideId;

            // Will be updating only text and tagged users
            const commentUpdates = {
                text,
                taggedUsers,
                pendingTaggedUsers
            };
            await CommentsController.updateComment(commentId, slideId, commentUpdates);

            const props = this.commentEventProps({ slideId, commentId });
            trackActivity("Comment", "Edited", null, null, props, { audit: true });
        } catch (err) {
            logger.error(err, "[comments] updateComment() failed");
            // Rethrow so the composer keeps the draft and the edit box stays open
            throw err;
        }
    }

    markCommentAsRead = async comment => {
        try {
            const commentId = comment.id;
            const slideId = comment.slideId;

            await CommentsController.markCommentAsRead(commentId, slideId);

            await AppController.notificationsService?.markAsReadByCommentId(commentId);
        } catch (err) {
            logger.error(err, "[comments] markCommentAsRead() failed");
        }
    }

    toggleLikeComment = async (comment, liked) => {
        try {
            await CommentsController.toggleLike(comment.id, comment.slideId, liked);

            const props = this.commentEventProps({ slideId: comment.slideId, commentId: comment.id });
            trackActivity("Comment", liked ? "Liked" : "Unliked", null, null, props, { audit: true });
        } catch (err) {
            logger.error(err, "[comments] toggleLikeComment() failed");
        }
    }

    resolveCommentThread = async (comment, resolved) => {
        try {
            await CommentsController.resolveComment(comment.id, comment.slideId, resolved);

            ShowSnackBar({ message: resolved ? "Comment resolved" : "Comment restored" });

            const props = this.commentEventProps({ slideId: comment.slideId, commentId: comment.id });
            trackActivity("Comment", resolved ? "Resolved" : "Reopened", null, null, props, { audit: true });
        } catch (err) {
            logger.error(err, "[comments] resolveCommentThread() failed");
        }
    }

    createReply = async (comment, text, taggedUsers = {}, pendingTaggedUsers = {}) => {
        try {
            const reply = {
                text,
                taggedUsers,
                pendingTaggedUsers
            };

            await CommentsController.createReply(comment.id, comment.slideId, reply);

            const props = this.commentEventProps({ slideId: comment.slideId, commentId: comment.id });
            trackActivity("Comment", "Replied", null, null, props, { audit: true });
        } catch (err) {
            logger.error(err, "[comments] createReply() failed");
            // Rethrow so the composer keeps the draft and resets its spinner instead of clearing
            throw err;
        }
    }

    updateReply = async (comment, replyId, text, taggedUsers = {}, pendingTaggedUsers = {}) => {
        try {
            await CommentsController.updateReply(comment.id, comment.slideId, replyId, { text, taggedUsers, pendingTaggedUsers });

            const props = this.commentEventProps({ slideId: comment.slideId, commentId: comment.id });
            trackActivity("Comment", "ReplyEdited", null, null, props, { audit: true });
        } catch (err) {
            logger.error(err, "[comments] updateReply() failed");
            // Rethrow so the composer keeps the draft and the edit box stays open
            throw err;
        }
    }

    deleteReply = async (comment, replyId) => {
        try {
            await CommentsController.deleteReply(comment.id, comment.slideId, replyId);

            const props = this.commentEventProps({ slideId: comment.slideId, commentId: comment.id });
            trackActivity("Comment", "ReplyDeleted", null, null, props, { audit: true });
        } catch (err) {
            logger.error(err, "[comments] deleteReply() failed");
        }
    }

    toggleReplyLike = async (comment, replyId, liked) => {
        try {
            await CommentsController.toggleLike(comment.id, comment.slideId, liked, replyId);

            const props = this.commentEventProps({ slideId: comment.slideId, commentId: comment.id });
            trackActivity("Comment", liked ? "ReplyLiked" : "ReplyUnliked", null, null, props, { audit: true });
        } catch (err) {
            logger.error(err, "[comments] toggleReplyLike() failed");
        }
    }

    handleCommentFilterChange = commentFilter => {
        this.setState({ commentFilter });
        this.props.onCommentFilterChange?.(commentFilter);
    }

    resolveUserName = uid => {
        if (!uid) {
            return null;
        }
        if (uid === this.user.uid) {
            return "You";
        }
        const user = WorkspaceController.users.find(u => u.uid === uid) ||
            this.collaborators.find(c => c.uid === uid) ||
            this.teammates.find(t => t.uid === uid);
        return user ? (user.displayName || user.email) : null;
    }

    render() {
        const {
            commentFilter,
            canEditPresentation,
            assignedUser,
            isPresentationOwner
        } = this.state;

        const {
            comments: rawComments,
            currentSlide,
            goToSlide,
            transitionState,
            presentation,
            byViewer
        } = this.props;

        const slideIndex = presentation.getSlideIndex(currentSlide.id);

        // Resolved comments are hidden from the This Slide / All Slides views and only
        // appear in the dedicated Resolved view (across all slides)
        const {
            displayedComments,
            slideComments,
            allComments,
            resolvedComments,
        } = getCommentsPaneLists({
            comments: rawComments,
            byViewer,
            commentFilter,
            currentSlideId: currentSlide.id,
        });

        const isResolvedView = commentFilter === CommentFilters.Resolved;

        return (
            <MuiThemeProvider theme={dialogTheme}>
                <FlexBox left middle>
                    <CommentFilterSelect
                        value={commentFilter}
                        onChange={event => this.handleCommentFilterChange(event.target.value)}
                        disableUnderline
                        SelectDisplayProps={{ "data-test-id": "comment-filter" }}
                    >
                        <StyledMenuItem value={CommentFilters.CurrentSlide}>
                            <DarkSelect>This Slide</DarkSelect>
                            &nbsp;
                            <span>({slideComments.length})</span>
                        </StyledMenuItem>
                        <StyledMenuItem value={CommentFilters.AllSlides}>
                            <DarkSelect>All Slides</DarkSelect>
                            &nbsp;
                            <span>({allComments.length})</span>
                        </StyledMenuItem>
                        <Divider component="li" />
                        <StyledMenuItem value={CommentFilters.Resolved}>
                            <DarkSelect>Resolved</DarkSelect>
                            &nbsp;
                            <span>({resolvedComments.length})</span>
                        </StyledMenuItem>
                    </CommentFilterSelect>
                    {
                        // Per-presentation notification settings live in the editor's Collaborate
                        // pane, which isn't reachable from the player, so hide the shortcut there
                        !byViewer &&
                        <Tooltip title="Notification settings" enterDelay={700} arrow>
                            <NotificationSettingsButton
                                data-test-id="comment-notification-settings"
                                onClick={() => AppController.showPresentationSettings(presentation.id, "collaborate", COMMENT_NOTIFICATIONS_SECTION_ID)}
                            >
                                notifications_none
                            </NotificationSettingsButton>
                        </Tooltip>
                    }
                </FlexBox>
                <CommentsList
                    ref={this.commentListRef}
                    className={classNames({
                        [`comment-list`]: true,
                        [`viewer`]: byViewer,
                        [`editor`]: !byViewer,
                    })}
                >
                    {
                        !isResolvedView &&
                        <Comment
                            isNew
                            key="newComment"
                            post={(text, taggedUsers, pendingTaggedUsers) => this.postComment(text, taggedUsers, pendingTaggedUsers)}
                            author={{ ...this.user, displayName: this.user.displayName || this.user.email }}
                            cancelAddingComment={() => this.setState({ isAddingComment: false })}
                            collaborators={this.collaborators}
                            teammates={this.teammates}
                            slideIndex={slideIndex}
                            byViewer={byViewer}
                            surface={this.props.surface}
                            assignedUser={assignedUser}
                            canEdit={transitionState === "entered"}
                            currentSlide={currentSlide}
                            presentation={presentation}
                        />
                    }
                    {
                        displayedComments.map(comment => (
                            <Comment
                                key={comment.id}
                                isMy={comment.authorUid === this.user.uid}
                                byViewer={comment.byViewer}
                                surface={this.props.surface}
                                isPresentationOwner={isPresentationOwner}
                                canEditPresentation={canEditPresentation}
                                remove={() => this.removeComment(comment)}
                                update={(text, taggedUsers, pendingTaggedUsers) => this.updateComment(comment, text, taggedUsers, pendingTaggedUsers)}
                                markAsRead={() => this.markCommentAsRead(comment)}
                                toggleLike={liked => this.toggleLikeComment(comment, liked)}
                                resolve={resolved => this.resolveCommentThread(comment, resolved)}
                                createReply={(text, taggedUsers, pendingTaggedUsers) => this.createReply(comment, text, taggedUsers, pendingTaggedUsers)}
                                updateReply={(replyId, text, taggedUsers, pendingTaggedUsers) => this.updateReply(comment, replyId, text, taggedUsers, pendingTaggedUsers)}
                                deleteReply={replyId => this.deleteReply(comment, replyId)}
                                toggleReplyLike={(replyId, liked) => this.toggleReplyLike(comment, replyId, liked)}
                                currentUserUid={this.user.uid}
                                resolveUserName={this.resolveUserName}
                                replies={comment.replies ? Object.entries(comment.replies).map(([id, reply]) => ({ ...reply, id })).sort((a, b) => a.createdAt - b.createdAt) : []}
                                likes={comment.likes}
                                likeCount={comment.likes ? Object.keys(comment.likes).length : 0}
                                likedByMe={!!(comment.likes && comment.likes[this.user.uid])}
                                resolved={!!comment.resolved}
                                replyCount={comment.replies ? Object.keys(comment.replies).length : 0}
                                text={comment.text}
                                slideIndex={presentation.getSlideIndex(comment.slideId)}
                                author={comment.author}
                                isRead={comment.readBy && comment.readBy[this.user.uid]}
                                createdAt={comment.createdAt}
                                assignedUser={assignedUser}
                                collaborators={this.collaborators}
                                teammates={this.teammates}
                                goToSlide={slideIndex => goToSlide(slideIndex)}
                                canEdit={transitionState === "entered"}
                                currentSlide={currentSlide}
                                presentation={presentation}
                            />
                        ))
                    }
                </CommentsList>
            </MuiThemeProvider>
        );
    }
}

const CommentsPaneWithPermissionsInitializer = PresentationPermissionsController.withStateInitializer(CommentsController.withInitializedState(CommentsPane), ({ presentation }) => presentation?.id);
const CommentsPaneWithPermissionsState = CommentsController.withInitializedState(PresentationPermissionsController.withState(CommentsPane));

function CommentsPaneWrapper(props) {
    const { byViewer, presentation, initializeController, firebaseUser } = props;

    useEffect(() => {
        if (!initializeController) {
            return;
        }

        CommentsController.initialize({
            presentationId: presentation.id,
            presentationLinkId: presentation.get("link")?.id,
            byViewer,
            firebaseUser
        });

        return () => {
            CommentsController.reset();
        };
    }, [presentation?.id, initializeController]);

    if (byViewer) {
        // The comments pane is rendered in the player, so we don't need the data
        // from the presentation permissions controller, keep it not initialized
        return <CommentsPaneWithPermissionsState {...props} />;
    }

    // Ensure the presentation permissions controller is initialized if comments are
    // viewed from the editor
    return <CommentsPaneWithPermissionsInitializer {...props} />;
}

export default withFirebaseUser(CommentsPaneWrapper);
